import React from 'react';
import { Box, Typography, Container, Grid, Paper, IconButton, Button, Stack } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { motion } from 'framer-motion';

const MusicCollection = () => {
  const deepGolden = "#B8860B";
  const royalGradient = "linear-gradient(135deg, #4B0082 0%, #800000 50%, #B8860B 100%)";

  const tracks = [
    { title: "नवकार मंत्र", artist: "पारंपारिक स्वर", duration: "05:20" },
    { title: "भक्तामर स्तोत्र", artist: "अनुराधा पौडवाल", duration: "12:45" },
    { title: "पार्श्वनाथ आरती", artist: "साधना सरगम", duration: "04:10" },
  ];

  return (
    <Box sx={{ 
      py: { xs: 6, md: 10 }, 
      background: royalGradient, 
      position: 'relative', 
      overflow: 'hidden',
      color: '#fff',
      minHeight: { xs: 'auto', md: '600px' },
      display: 'flex',
      alignItems: 'center'
    }}>
      
      {/* बॅकग्राउंड ॐ वॉटरमार्क - मोबाईलवर अधिक ट्रान्सपरंट */}
      <Box 
        sx={{
          position: 'absolute',
          right: { xs: '10%', md: '-5%' },
          bottom: { xs: '5%', md: '-10%' },
          opacity: { xs: 0.05, md: 0.1 }, 
          fontSize: { xs: '250px', md: '450px' },
          color: '#fff',
          fontWeight: 900,
          zIndex: 1,
          pointerEvents: 'none',
          fontFamily: 'serif'
        }}
      >
        ॐ
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" justifyContent="center">
          
          {/* --- डावी बाजू: सात्विक टायटल आणि वर्णन --- */}
          <Grid item xs={12} md={5}>
            <Box 
              component={motion.div}
              initial={{ opacity: 0, y: -20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              sx={{ 
                textAlign: { xs: 'center', md: 'left' }, // मोबाईलवर सेंटर
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-start' } // मोबाईलवर बटण सेंटर
              }}
            >
              <Typography variant="overline" sx={{ color: '#FFE4B5', fontWeight: 800, letterSpacing: 2, mb: 1 }}>
                आत्मिक शांतीसाठी...
              </Typography>
              
              <Typography variant="h2" sx={{ 
                fontWeight: 900, 
                fontFamily: "'serif'", 
                mb: 2.5,
                lineHeight: 1.2,
                fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.5rem' },
                textShadow: '2px 4px 10px rgba(0,0,0,0.3)'
              }}>
                सुमधुर <br /> <span style={{ color: '#FFD700' }}>भक्ती रस-धारा</span>
              </Typography>

              <Typography variant="body1" sx={{ 
                opacity: 0.9, 
                mb: 4, 
                lineHeight: 1.8, 
                fontSize: { xs: '1rem', md: '1.1rem' }, 
                maxWidth: '500px',
                fontStyle: 'italic'
              }}>
                परम पूज्य स्वरांच्या माध्यमातून आपल्या अंतर्मनाला अध्यात्माशी जोडा. येथे आपणास मिळतील भावपूर्ण आरत्या, प्रभावशाली स्तोत्र आणि मनाला शांती देणारी भक्तीगीते.
              </Typography>

              <Button 
                variant="outlined" 
                size="large"
                startIcon={<MusicNoteIcon />}
                sx={{ 
                  color: '#fff', 
                  borderColor: 'rgba(255,255,255,0.5)', 
                  borderRadius: '30px', 
                  px: 4, py: 1.5,
                  fontWeight: 800,
                  textTransform: 'none',
                  background: 'rgba(255,255,255,0.05)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: '#FFD700' }
                }}
              >
                सर्व भक्ती रचना पहा
              </Button>
            </Box>
          </Grid>

          {/* --- उजवी बाजू: म्युझिक ट्रॅक्स लिस्ट (मोबाईलवर सेंटर) --- */}
          <Grid item xs={12} md={7}>
            <Stack spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
              {tracks.map((track, index) => (
                <Box
                  key={index}
                  component={motion.div}
                  initial={{ opacity: 0, x: { xs: 0, md: 50 } }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  sx={{ width: '100%', maxWidth: { xs: '100%', sm: '500px', md: '100%' } }}
                >
                  <Paper sx={{ 
                    p: { xs: 2, sm: 2.5 }, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    background: 'rgba(255, 255, 255, 0.1)', 
                    backdropFilter: 'blur(12px)',
                    borderRadius: '20px',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    color: '#fff',
                    transition: '0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': { 
                      background: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 30px rgba(0,0,0,0.2)'
                    }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {/* प्ले बटन */}
                      <IconButton sx={{ 
                        bgcolor: deepGolden, 
                        color: '#fff', 
                        width: 42, height: 42,
                        '&:hover': { bgcolor: '#946b08' } 
                      }}>
                        <PlayArrowIcon sx={{ fontSize: '1.4rem' }} />
                      </IconButton>
                      
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>
                          {track.title}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7, letterSpacing: 0.5 }}>
                          {track.artist}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 3 } }}>
                      <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, opacity: 0.8 }}>
                        {track.duration}
                      </Typography>
                      <IconButton sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#FFD700' } }}>
                        <FavoriteBorderIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                </Box>
              ))}
            </Stack>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default MusicCollection;